// Image d'un monstre à dessiner à un instant donné : l'image courante de son animation, prise dans sa planche (au
// repos ou de dos, voir pickAnimation), ou son image fixe. Partagé par le jeu (combat, suiveur, monstres posés) et
// l'administration.
import { pickAnimation, type MonsterSprite, type SpriteView } from '../../shared/monster-sprites.ts';

/** Filtre des chromatiques qui n'ont pas d'animation dédiée (le même que `img.shiny` dans les feuilles de style). */
export const SHINY_FILTER = 'hue-rotate(150deg) saturate(1.4) brightness(1.1)';

export interface SpriteSource {
  image: string;
  sprite: MonsterSprite | null;
  shiny: boolean;
}

/** Morceau d'image à dessiner ; `back` : c'est vraiment une vue de dos (à ne pas retourner comme la vue de face). */
export interface SpriteFrame {
  image: HTMLImageElement;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  filter: boolean;
  back: boolean;
}

const images = new Map<string, HTMLImageElement>();

/** Image fixe ou planche d'une espèce, chargée une seule fois. */
export function monsterImage(src: string): HTMLImageElement {
  let image = images.get(src);
  if (!image) {
    image = Object.assign(new Image(), { src });
    images.set(src, image);
  }
  return image;
}

/** Ce qu'il faut dessiner à l'instant `now` (millisecondes) ; null tant que l'image charge. */
export function spriteFrame(source: SpriteSource, view: SpriteView, now: number): SpriteFrame | null {
  const { sprite } = source;
  const pick = pickAnimation(sprite, view, source.shiny);
  if (sprite && pick.frames) {
    const sheet = monsterImage(sprite.sheet);
    if (!sheet.complete || !sheet.naturalWidth) return null;
    const frame = pick.frames[Math.floor(now / sprite.frameMs) % pick.frames.length]!;
    return { image: sheet, sx: frame.x, sy: frame.y, sw: sprite.width, sh: sprite.height, filter: pick.filter, back: pick.back };
  }
  const image = monsterImage(source.image);
  if (!image.complete || !image.naturalWidth) return null;
  return { image, sx: 0, sy: 0, sw: image.naturalWidth, sh: image.naturalHeight, filter: source.shiny, back: false };
}

/**
 * Dessine le morceau d'image dans le rectangle donné ; `filter` : appliquer le filtre chromatique s'il le faut (sinon,
 * c'est à l'appelant de le faire, par une classe CSS sur le canvas par exemple).
 */
export function drawSpriteFrame(ctx: CanvasRenderingContext2D, frame: SpriteFrame, dx: number, dy: number, dw: number, dh: number, filter = true): void {
  ctx.save();
  if (filter && frame.filter) ctx.filter = SHINY_FILTER;
  // Pixel art net quand il est agrandi, lissage pour une grande image réduite.
  ctx.imageSmoothingEnabled = dw < frame.sw;
  ctx.drawImage(frame.image, frame.sx, frame.sy, frame.sw, frame.sh, dx, dy, dw, dh);
  ctx.restore();
}

/**
 * Remplit un canvas (à sa taille affichée) avec le monstre, contenu et centré, et lui met la classe `shiny` quand le
 * filtre chromatique s'applique ; renvoie ce qui a été dessiné (null : image en cours de chargement).
 */
export function paintSpriteCanvas(canvas: HTMLCanvasElement, source: SpriteSource, view: SpriteView, now: number): SpriteFrame | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const ratio = window.devicePixelRatio || 1;
  const width = Math.round(canvas.clientWidth * ratio), height = Math.round(canvas.clientHeight * ratio);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  ctx.clearRect(0, 0, width, height);
  const frame = spriteFrame(source, view, now);
  canvas.classList.toggle('shiny', frame?.filter ?? false);
  if (!frame || !width || !height) return frame;
  const scale = Math.min(width / frame.sw, height / frame.sh);
  const dw = frame.sw * scale, dh = frame.sh * scale;
  drawSpriteFrame(ctx, frame, (width - dw) / 2, (height - dh) / 2, dw, dh, false);
  return frame;
}
